import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, X as XIcon, ZoomIn } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Resolve a stored image path to a fully-qualified URL.
 * - Paths beginning with `/api/files/…` are routed through our backend.
 * - Anything already absolute (https://…) is returned as-is.
 */
function resolveSrc(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api/")) return `${API_BASE}${path}`;
  return path;
}

/**
 * Product image gallery. Shows the main image as the lead tile, with a
 * horizontally-scrolling strip of thumbs underneath. Clicking any tile opens
 * a lightbox with prev/next + keyboard arrow nav. Designed for the
 * /choose-your-door product cards but reusable anywhere a list of photos
 * exists on a product (`gallery_images` on the Product model).
 */
export default function ProductGallery({ mainImage, images = [], productName = "" }) {
  const allImages = [mainImage, ...(images || [])].filter(Boolean);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const open = (idx) => {
    setActiveIdx(idx);
    setLightboxOpen(true);
  };
  const close = useCallback(() => setLightboxOpen(false), []);
  const prev = useCallback(
    () => setActiveIdx((i) => (i - 1 + allImages.length) % allImages.length),
    [allImages.length]
  );
  const next = useCallback(
    () => setActiveIdx((i) => (i + 1) % allImages.length),
    [allImages.length]
  );

  // Keyboard nav while lightbox is open. Esc closes, ←/→ navigate.
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen, close, prev, next]);

  if (allImages.length === 0) return null;

  // Single-image (no extras) → render the same tile shape but without the
  // strip / zoom affordance, so the layout stays consistent across tiers.
  const hasGallery = allImages.length > 1;

  return (
    <div data-testid="product-gallery">
      <button
        type="button"
        onClick={() => open(0)}
        disabled={!hasGallery}
        className={`relative w-full rounded-2xl overflow-hidden border border-[var(--brand-border)] flex items-center justify-center group ${
          hasGallery ? "cursor-zoom-in" : ""
        }`}
        style={{ background: "#f5f7f6", aspectRatio: "3 / 4" }}
        data-testid="gallery-main-tile"
      >
        <img
          src={resolveSrc(allImages[0])}
          alt={productName}
          loading="lazy"
          className="h-full w-full object-contain p-3 transition-transform group-hover:scale-[1.02]"
        />
        {hasGallery && (
          <span
            className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-black/70 text-white text-[11px] font-semibold rounded-full px-2.5 py-1"
            data-testid="gallery-count-pill"
          >
            <ZoomIn className="w-3 h-3" />
            {allImages.length} photos
          </span>
        )}
      </button>

      {hasGallery && (
        <div
          data-testid="gallery-thumbs"
          // 3-per-row grid keeps thumbs inside the viewport on iPhone.
          // Bumps to 4-6 on wider screens so 6-photo galleries don't wrap
          // awkwardly on desktop.
          className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2"
        >
          {allImages.map((src, i) => (
            <button
              key={i}
              type="button"
              data-testid={`gallery-thumb-${i}`}
              onClick={() => open(i)}
              className="rounded-lg overflow-hidden border-2 border-[var(--brand-border)] hover:border-[var(--brand-green)] transition flex items-center justify-center"
              style={{ background: "#f5f7f6", aspectRatio: "1 / 1" }}
              aria-label={`Open photo ${i + 1}`}
            >
              <img
                src={resolveSrc(src)}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {lightboxOpen && (
        <div
          data-testid="gallery-lightbox"
          onClick={close}
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
        >
          {/* Top bar: counter on the left, BIG labelled close on the right.
              Pushed below the iOS safe-area inset so Safari's URL bar can't
              cover it on iPhone. */}
          <div
            className="absolute left-0 right-0 flex items-center justify-between px-4 py-3 z-10"
            style={{ top: "max(0px, env(safe-area-inset-top))" }}
            onClick={(e) => e.stopPropagation()}
          >
            <span
              data-testid="gallery-counter"
              className="text-white/90 text-xs font-semibold tabular-nums bg-black/40 backdrop-blur px-2.5 py-1 rounded-full"
            >
              {activeIdx + 1} / {allImages.length}
            </span>
            <button
              type="button"
              data-testid="gallery-close"
              onClick={close}
              className="flex items-center gap-1.5 bg-white text-black font-semibold text-sm px-4 py-2 rounded-full shadow-lg hover:bg-white/90 transition"
              aria-label="Close gallery"
            >
              <XIcon className="w-4 h-4" />
              Close
            </button>
          </div>

          {allImages.length > 1 && (
            <>
              <button
                type="button"
                data-testid="gallery-prev"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="Previous photo"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                data-testid="gallery-next"
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="Next photo"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <img
            src={resolveSrc(allImages[activeIdx])}
            alt={`${productName} — photo ${activeIdx + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[80vh] max-w-[92vw] object-contain rounded-xl"
            data-testid="gallery-current-image"
          />

          {/* Mobile-friendly secondary close at bottom — easier to thumb-
              reach than the corner button on a phone. */}
          <button
            type="button"
            data-testid="gallery-close-bottom"
            onClick={close}
            className="absolute left-1/2 -translate-x-1/2 bg-white text-black font-semibold text-sm px-5 py-2.5 rounded-full shadow-lg z-10"
            style={{ bottom: "max(16px, env(safe-area-inset-bottom))" }}
            aria-label="Close gallery"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}
